// Runtime configuration, fetched from /config.json so one image serves every environment: the
// deployment mounts its own file (ConfigMap) and adding an organisation needs no rebuild.

export interface Organisation {
  /** Full name shown to people, e.g. "EYDAP – Athens Water Supply and Sewerage". */
  name: string;
  /** Keycloak realm the organisation's users sign in to, e.g. "endurance-eydap". */
  realm: string;
}

export interface AppConfig {
  keycloakUrl: string;
  /** KB API base, e.g. "https://kb-01.endurance.lab.synelixis.com/api" or "/api" behind a proxy. */
  apiUrl: string;
  /** Public PKCE client in every realm; carries the kb-audience scope the KB validates. */
  clientId: string;
  /** Who to contact about access, shown in plain-language error messages. */
  supportContact: string;
  organisations: Organisation[];
}

export class ConfigError extends Error {}

export function parseConfig(raw: unknown): AppConfig {
  const c = raw as Partial<AppConfig> | null;
  if (!c || typeof c !== 'object') throw new ConfigError('config.json is not an object');
  for (const key of ['keycloakUrl', 'apiUrl', 'clientId'] as const) {
    if (typeof c[key] !== 'string' || !c[key]) throw new ConfigError(`config.json: ${key} is missing`);
  }
  if (!Array.isArray(c.organisations) || c.organisations.length === 0) {
    throw new ConfigError('config.json: organisations is empty');
  }
  const organisations = c.organisations.filter(
    (o): o is Organisation => !!o && typeof o.name === 'string' && typeof o.realm === 'string' && !!o.realm,
  );
  return {
    keycloakUrl: c.keycloakUrl!.replace(/\/+$/, ''),
    apiUrl: c.apiUrl!.replace(/\/+$/, ''),
    clientId: c.clientId!,
    supportContact: typeof c.supportContact === 'string' && c.supportContact ? c.supportContact : 'the Knowledge Base operator',
    organisations: [...organisations].sort((a, b) => a.name.localeCompare(b.name)),
  };
}

export async function loadConfig(fetchImpl: typeof fetch = fetch): Promise<AppConfig> {
  const response = await fetchImpl('/config.json', { cache: 'no-store' });
  if (!response.ok) throw new ConfigError(`config.json: HTTP ${response.status}`);
  return parseConfig(await response.json());
}
